// converts a Date to "YYYY-MM" string format used as month keys
export function yearMonthString(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

// Truncate text to specified length with ellipsis
export function truncate(text, maxLength = 35) {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

// shift a "YYYY-MM" string by N months (negative = earlier)
export function shiftMonth(monthStr, delta) {
  const [y, m] = monthStr.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return yearMonthString(d);
}

/** Shortens location display - e.g., "Salt Lake City, Utah, United States of America" → "Salt Lake City, Utah, USA" */
export function formatLocation(location) {
  if (!location) return '';
  return location
    .replace(/\s*\(the\)\s*/gi, '')
    .replace(/United States of America/gi, 'USA')
    .replace(/United States/gi, 'USA')
    .replace(/USA, USA/gi, 'USA');
}

/** Check if profile has minimum required fields (image, name, location, age) */
export function isProfileComplete(p) {
  return p?.name && p?.location && p?.stats?.age && p?.images?.length > 0;
}

// Format timestamp to shorthand:
// Today: "2:30 PM"
// This year: "2:30 PM Tues May 29"
// Another year: "2:30 PM Tues May 29, 2025"
export function formatTimeShort(iso) {
  if (!iso) return '';

  const date = new Date(iso);
  const now = new Date();

  const isToday = date.toDateString() === now.toDateString();
  const isSameYear = date.getFullYear() === now.getFullYear();

  const timeStr = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const dayStr = date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
  const yearStr = date.toLocaleDateString([], { year: 'numeric' });

  if (isToday) {
    // Just show time (e.g., "2:30 PM")
    return timeStr;
  } else if (isSameYear) {
    // Show time, day of week, month and day (e.g., "2:30 PM Tues May 29")
    return `${timeStr} ${dayStr}`;
  } else {
    // Show time, day of week, month, day, and year (e.g., "2:30 PM Tues May 29, 2025")
    return `${timeStr} ${dayStr}, ${yearStr}`;
  }
}

// Check if calendar is shared with current user
export function isCalendarShared(receivedShares, otherUserId, currentUserId) {
  return receivedShares && Object.values(receivedShares).some(s => s.owner_id === otherUserId && s.shared_with_id === currentUserId);
}

// Check if user has shared their calendar with someone else (user is owner)
export function sharedTo(userId, ownedShares) {
  if (!ownedShares) return false;
  return Object.keys(ownedShares).length > 0;
}

// Check if user has received a calendar share from a specific other user
export function sharedFrom(currentUserId, receivedShares, otherUserId) {
  if (!receivedShares) return false;
  if (!otherUserId) return Object.keys(receivedShares).length > 0; // Fallback to old behavior
  return Object.values(receivedShares).some(share => share.owner_id === otherUserId && share.shared_with_id === currentUserId);
}

// Get initial date range (6 months centered around today)
export function getInitialDateRange() {
  const now = new Date();
  const prev = new Date(now.getFullYear(), now.getMonth() - 3, 1);
  const next = new Date(now.getFullYear(), now.getMonth() + 3, 1);
  return {
    startMonth: yearMonthString(prev),
    endMonth: yearMonthString(next),
  };
}

// Find chat ID between two users
export function findChatId(chats, userId1, userId2) {
  if (!userId1 || !userId2) return null;
  const chat = Object.values(chats).find(chat =>
    chat.user_ids?.includes(userId1) && chat.user_ids?.includes(userId2)
  );
  return chat?.id || null;
}

// Determine available tabs based on user role and share status
export function determineAvailableTabs(userRole, receivedShares, otherUserId, currentUserId) {
  const tabs = [
    { name: 'chat', disabled: false },
    { name: 'profile', disabled: false },
  ];

  const isShared = isCalendarShared(receivedShares, otherUserId, currentUserId);
  tabs.push({ name: 'calendar', disabled: !isShared });

  return tabs;
}

/**
 * Get cycle day for a test
 * First checks if cycleDay (camelCase) is already in the test data, otherwise looks it up from calendar data
 * @param {Object} test - Test object with taken_at timestamp
 * @param {Object} calendarData - Calendar data object with date keys
 * @returns {number|null} Cycle day or null if not found
 */
export function getTestCD(test, calendarData) {
  // If cycleDay (camelCase) is already in the test data, use it
  if (test.cycleDay !== null && test.cycleDay !== undefined) {
    return test.cycleDay;
  }

  // Otherwise, look it up from calendar data
  if (!test.taken_at) return null;

  const testDate = new Date(test.taken_at);
  // Use local methods to match calendar data keys (which are in local time)
  const year = testDate.getFullYear();
  const month = String(testDate.getMonth() + 1).padStart(2, '0');
  const day = String(testDate.getDate()).padStart(2, '0');
  const testDateStr = `${year}-${month}-${day}`;

  // If calendarData exists, look up the cycle day
  if (calendarData) {
    const cycleDay = calendarData?.[testDateStr]?.ipData?.cd;
    if (cycleDay !== null && cycleDay !== undefined) {
      return cycleDay;
    }
  }

  // If no calendar data or cycle day not found, return null
  return null;
}

/**
 * Convert UTC timestamp to local date string (YYYY-MM-DD)
 * @param {string} timestamp - UTC timestamp string
 * @returns {string} Local date string in YYYY-MM-DD format
 */
export function utcToLocalDateString(timestamp) {
  if (!timestamp) return null;
  const date = new Date(timestamp);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Check if two timestamps are on different days (local time)
 * @param {string} timestamp1 - First timestamp
 * @param {string} timestamp2 - Second timestamp
 * @returns {boolean} True if different days
 */
export function isDifferentDay(timestamp1, timestamp2) {
  if (!timestamp1 || !timestamp2) return true;
  return utcToLocalDateString(timestamp1) !== utcToLocalDateString(timestamp2);
}
